/**
 * The pilot brain boundary: what makes an aircraft speak, and what it may do about it.
 *
 * The sim decides *when* a pilot has something to say and applies every consequence (radio log,
 * requests, score, holds). A brain only decides *what* is said and what the crew wants. The rules
 * brain (`rules-brain.ts`) is the default and speaks the sim's own phraseology; any other brain,
 * including a model, must speak in phrase parts that `checkParts` has approved.
 */
import type { AirportData, RealismOptions } from '@atc/core';
import type { Aircraft } from '../aircraft';
import type { Instruction } from '../commands';
import type { EmergencyIntention, EmergencyLevel, EmergencyNature } from '../emergency';
import type { RadioKind, Phrase } from '../comms';
import type { RequestKind } from '../patience';
import type { ConversationTopic, QuerySubject } from '../pilot-brain';
import type { Rng } from '../rng';
import type { PhrasePart } from './phrase';
import type { PilotSituation } from './situation';

/** Why the pilot is speaking. The sim raises these; it never asks a brain whether to speak. */
export type PilotTrigger =
  /** First call on the frequency. */
  | { kind: 'checkin' }
  /** Something happened to the aircraft that a pilot reports. */
  | { kind: 'event'; event: 'clearOfRunway'; runway: string }
  | { kind: 'event'; event: 'established'; runway: string; cleared: boolean }
  | { kind: 'event'; event: 'requestLanding'; runway: string }
  | { kind: 'event'; event: 'requestClimb' }
  | { kind: 'event'; event: 'missedIntercept' }
  | { kind: 'event'; event: 'goAround'; reason: string }
  | { kind: 'event'; event: 'endOfRoute'; fix: string }
  | { kind: 'event'; event: 'offRoute'; fix: string }
  /** Reached the fix it was cleared to, which has a published hold: the crew enters it and asks for the approach. */
  | { kind: 'event'; event: 'clearanceLimit'; fix: string }
  /** About to cross the field with no approach clearance and no heading: the crew holds (at `fix`, when there is one) and says so. */
  | { kind: 'event'; event: 'noApproachClearance'; fix?: string }
  /** Nothing is wrong, but a real crew would ask by now. */
  | { kind: 'initiative'; want: 'holdingShort' | 'approach' | 'descent' | 'vectors' }
  /** On a SID heading leg below the next fix's lowest altitude: the crew cannot continue the departure without a climb. */
  | { kind: 'initiative'; want: 'sidClimb'; altitudeFt: number }
  /** An unanswered request: call again, press, or act alone. */
  | { kind: 'patience'; stage: 1 | 2 | 3; need: RequestKind; holdFix?: string }
  /** Read back what the pilot will fly; at high realism that may not be what was said. */
  | { kind: 'readback'; instruction: Instruction }
  /** The sim has already refused the instruction; the pilot only has to say so. */
  | { kind: 'unable'; instruction: Instruction; reason: string }
  /** A question, answered from the sim's own facts. */
  | { kind: 'answer'; subject: QuerySubject }
  /** Free-form, aviation-relevant speech; it may produce words but never a flight action. */
  | { kind: 'conversation'; topic: ConversationTopic; text: string }
  /** A call that needs no more than an acknowledgement. */
  | { kind: 'reply'; to: 'standby' | 'acknowledge' }
  /**
   * Fuel has reached a state the controller must be told about: `minimum` means no further delay
   * can be accepted, `mayday` is a distress call, `exhausted` is the engines stopping.
   */
  | { kind: 'fuel'; state: 'minimum' | 'mayday' | 'exhausted'; minutesLeft: number; runway: string }
  /** Something has gone wrong on board and the controller must be told. */
  | { kind: 'emergency'; nature: EmergencyNature; level: EmergencyLevel; intention: EmergencyIntention; runway: string };

/** What the crew does about it. The sim applies these; nothing here changes the flight path directly. */
export type PilotIntent =
  /**
   * Say it. `parts` is the checked form every brain may use; `phrase` is the sim's own rendered
   * phraseology and is accepted only from the rules brain (see `validateDecision`).
   */
  | { kind: 'say'; parts: PhrasePart[]; form: 'pilot' | 'plain'; radio: RadioKind; phrase?: undefined }
  | { kind: 'say'; phrase: Phrase; radio: RadioKind; parts?: undefined; form?: undefined }
  /** Start waiting for something; the sim ignores it when pilot patience is off. */
  | { kind: 'request'; request: RequestKind; deferS?: number; unvoiced?: boolean }
  /** Act alone after being ignored. */
  | { kind: 'act'; action: 'hold' | 'returnToStand'; fix?: string }
  | { kind: 'silence' };

export interface PilotDecision {
  intents: PilotIntent[];
  source: 'rules' | 'llm' | 'replay' | 'fallback';
}

/**
 * What the brain may look at. Live sim objects: a synchronous in-process brain reads them
 * directly, and an out-of-process brain gets the detached situation built for it in `situation.ts`.
 */
export interface PilotContext {
  ac: Aircraft;
  airport: AirportData;
  realism: RealismOptions;
  simTimeS: number;
  telephony?: Record<string, string>;
  /** The wording stream: its own seed, so varied phrasing never shifts traffic or timing. */
  rng?: Rng;
  /** Local hour at the airport (0–24), when the host set a clock (`sim.setLocalClock`); drives greetings. */
  localHour?: number;
}

export type { PilotSituation } from './situation';

export interface PilotBrain {
  readonly id: string;
  readonly mode?: 'sync';
  decide(trigger: PilotTrigger, ctx: PilotContext): PilotDecision;
}

/** What an asynchronous brain is asked. The situation is plain JSON; it can be sent anywhere. */
export interface BrainRequest {
  id: number;
  trigger: PilotTrigger;
  situation: PilotSituation;
  /** Sim time by which the answer must be delivered, or the rules brain speaks instead. */
  deadlineS: number;
}

/**
 * A brain that answers later, through `Sim.deliverDecision(id, decision, usage)`. The sim carries
 * on either way: nothing blocks on it, and a late answer is dropped.
 */
export interface AsyncPilotBrain {
  readonly id: string;
  readonly mode: 'async';
  request(req: BrainRequest): void;
  /** The answer is no longer wanted (the aircraft is gone, or the deadline passed). */
  cancel?(id: number): void;
}

export type AnyPilotBrain = PilotBrain | AsyncPilotBrain;
export const isAsyncBrain = (b: AnyPilotBrain): b is AsyncPilotBrain => b.mode === 'async';
